import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { categoryService } from '../../../services/categoryService';

const ReconciliationPanel = ({ transactions, account, onReconciliationComplete, onClose }) => {
  const [statementBalance, setStatementBalance] = useState('');
  const [clearedTransactions, setClearedTransactions] = useState(
    new Set(transactions?.filter(t => t?.cleared)?.map(t => t?.id))
  );
  const [showReconciliationOptions, setShowReconciliationOptions] = useState(false);
  const [showAddTransactionForm, setShowAddTransactionForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  const [newTransactionForm, setNewTransactionForm] = useState({
    date: new Date()?.toISOString()?.split('T')?.[0],
    description: '',
    amount: '',
    type: 'expense',
    categoryId: ''
  });

  const getCurrencySymbol = () => {
    return '$';
  };

  const clearedBalance = useMemo(() => {
    return transactions
      ?.filter(t => clearedTransactions?.has(t?.id))
      ?.reduce((sum, t) => sum + t?.amount, 0);
  }, [transactions, clearedTransactions]);

  const unclearedTransactions = useMemo(() => {
    return transactions?.filter(t => !clearedTransactions?.has(t?.id));
  }, [transactions, clearedTransactions]);

  const difference = useMemo(() => {
    if (!statementBalance) return 0;
    return parseFloat(statementBalance) - clearedBalance;
  }, [statementBalance, clearedBalance]);

  const isBalanced = Math.abs(difference) < 0.01;

  const toggleCleared = (transactionId) => {
    setClearedTransactions(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(transactionId)) {
        newSet?.delete(transactionId);
      } else {
        newSet?.add(transactionId);
      }
      return newSet;
    });
  };

  const handleReconcile = () => {
    if (isBalanced) {
      onReconciliationComplete({
        newBalance: parseFloat(statementBalance),
        clearedTransactionIds: Array.from(clearedTransactions),
        reconciledAt: new Date()?.toISOString()
      });
    } else {
      // Show options when not balanced
      setShowReconciliationOptions(true);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await categoryService?.getAll();
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddMissingTransaction = () => {
    loadCategories();
    setNewTransactionForm({
      date: new Date()?.toISOString()?.split('T')?.[0],
      description: '',
      amount: Math.abs(difference)?.toFixed(2),
      type: difference > 0 ? 'income' : 'expense',
      categoryId: ''
    });
    setShowAddTransactionForm(true);
    setShowReconciliationOptions(false);
  };

  const handleCreateReconciliationAdjustment = () => {
    // Create a reconciliation adjustment transaction
    const adjustmentTransaction = {
      date: new Date()?.toISOString()?.split('T')?.[0],
      description: 'Reconciliation Adjustment',
      amount: difference,
      type: difference > 0 ? 'income' : 'expense',
      categoryId: null,
      cleared: true,
      isReconciliationAdjustment: true
    };

    onReconciliationComplete({
      newBalance: parseFloat(statementBalance),
      clearedTransactionIds: Array.from(clearedTransactions),
      reconciledAt: new Date()?.toISOString(),
      adjustmentTransaction
    });
    setShowReconciliationOptions(false);
  };

  const handleSubmitNewTransaction = () => {
    if (!newTransactionForm?.description || !newTransactionForm?.amount) {
      alert('Please fill in all required fields');
      return;
    }

    const newTransaction = {
      date: newTransactionForm?.date,
      description: newTransactionForm?.description,
      amount: parseFloat(newTransactionForm?.amount) * (newTransactionForm?.type === 'expense' ? -1 : 1),
      type: newTransactionForm?.type,
      categoryId: newTransactionForm?.categoryId || null,
      cleared: true
    };

    onReconciliationComplete({
      newBalance: parseFloat(statementBalance),
      clearedTransactionIds: Array.from(clearedTransactions),
      reconciledAt: new Date()?.toISOString(),
      newTransaction
    });
    setShowAddTransactionForm(false);
  };

  const filteredCategories = categories?.filter(
    cat => cat?.type === newTransactionForm?.type || cat?.type === 'both'
  );

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 sticky top-24">
      <div className="p-20 md:p-24 border-b border-border">
        <div className="flex items-center justify-between mb-16">
          <h3 className="text-lg font-heading font-semibold text-card-foreground">
            Reconciliation
          </h3>
          <button
            onClick={onClose}
            className="p-6 hover:bg-muted rounded transition-smooth"
            title="Close"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground font-caption">
          Match your transactions with your bank statement
        </p>
      </div>
      <div className="p-20 md:p-24 space-y-20">
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Statement Ending Balance *
          </label>
          <Input
            type="number"
            step="0.01"
            value={statementBalance}
            onChange={(e) => setStatementBalance(e?.target?.value)}
            placeholder="0.00"
          />
        </div>

        <div className="bg-muted rounded-lg p-16 space-y-12">
          <div className="flex justify-between items-center">
            <span className="text-sm font-caption text-muted-foreground">Current Balance</span>
            <span className="text-sm font-caption font-medium text-foreground">
              {getCurrencySymbol()}{account?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-caption text-muted-foreground">Cleared Balance</span>
            <span className="text-sm font-caption font-medium text-foreground">
              {getCurrencySymbol()}{clearedBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          {statementBalance && (
            <>
              <div className="border-t border-border pt-12">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-caption font-medium text-foreground">Difference</span>
                  <span className={`text-sm font-caption font-semibold ${
                    isBalanced ? 'text-success' : 'text-error'
                  }`}>
                    {getCurrencySymbol()}{Math.abs(difference)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <div className={`flex items-center gap-8 p-12 rounded-lg ${
                isBalanced ? 'bg-success/10' : 'bg-error/10'
              }`}>
                <Icon 
                  name={isBalanced ? 'CheckCircle2' : 'AlertCircle'} 
                  size={16} 
                  color={isBalanced ? 'var(--color-success)' : 'var(--color-error)'} 
                />
                <span className={`text-xs font-caption font-medium ${
                  isBalanced ? 'text-success' : 'text-error'
                }`}>
                  {isBalanced ? 'Account is balanced!' : 'Account is not balanced'}
                </span>
              </div>
            </>
          )}
        </div>

        <div>
          <h4 className="text-sm font-caption font-semibold text-foreground mb-12">
            Uncleared Transactions ({unclearedTransactions?.length})
          </h4>
          <div className="space-y-8 max-h-[400px] overflow-y-auto">
            {unclearedTransactions?.length === 0 ? (
              <div className="text-center py-20">
                <Icon name="CheckCircle2" size={32} color="var(--color-success)" className="mx-auto mb-8" />
                <p className="text-sm text-muted-foreground font-caption">
                  All transactions cleared
                </p>
              </div>
            ) : (
              unclearedTransactions?.map(transaction => (
                <div
                  key={transaction?.id}
                  className="flex items-center justify-between p-12 bg-background rounded-lg hover:bg-muted transition-smooth cursor-pointer"
                  onClick={() => toggleCleared(transaction?.id)}
                >
                  <div className="flex items-center gap-12 flex-1 min-w-0">
                    <div className={`flex items-center justify-center w-8 h-8 rounded ${
                      transaction?.type === 'income' ? 'bg-success/10' :
                      transaction?.type === 'expense' ? 'bg-error/10' : 'bg-accent/10'
                    }`}>
                      <Icon 
                        name={
                          transaction?.type === 'income' ? 'ArrowDownLeft' :
                          transaction?.type === 'expense' ? 'ArrowUpRight' : 'ArrowLeftRight'
                        }
                        size={12}
                        color={
                          transaction?.type === 'income' ? 'var(--color-success)' :
                          transaction?.type === 'expense' ? 'var(--color-error)' : 'var(--color-accent)'
                        }
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-caption font-medium text-foreground truncate">
                        {transaction?.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(transaction?.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-8">
                    <span className={`text-xs font-caption font-semibold ${
                      transaction?.amount >= 0 ? 'text-success' : 'text-error'
                    }`}>
                      {transaction?.amount >= 0 ? '+' : ''}{getCurrencySymbol()}{Math.abs(transaction?.amount)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      clearedTransactions?.has(transaction?.id) 
                        ? 'bg-primary border-primary' :'border-muted-foreground'
                    }`}>
                      {clearedTransactions?.has(transaction?.id) && (
                        <Icon name="Check" size={12} color="white" />
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <Button
          variant="default"
          fullWidth
          disabled={!statementBalance}
          onClick={handleReconcile}
          iconName="CheckCircle2"
          iconPosition="left"
          iconSize={18}
        >
          {isBalanced ? 'Complete Reconciliation' : 'Continue'}
        </Button>
      </div>
      {/* Reconciliation Options Modal */}
      {showReconciliationOptions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
          <div className="bg-card rounded-lg shadow-elevation-4 max-w-md w-full">
            <div className="p-24 border-b border-border">
              <h3 className="text-lg font-heading font-semibold text-card-foreground mb-8">
                Account Not Balanced
              </h3>
              <p className="text-sm text-muted-foreground">
                There's a difference of {getCurrencySymbol()}{Math.abs(difference)?.toFixed(2)}. How would you like to proceed?
              </p>
            </div>
            <div className="p-24 space-y-12">
              <button
                onClick={handleAddMissingTransaction}
                className="w-full p-16 bg-background hover:bg-muted rounded-lg border border-border transition-smooth text-left"
              >
                <div className="flex items-start gap-12">
                  <div className="p-8 bg-primary/10 rounded-lg">
                    <Icon name="Plus" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-foreground mb-4">
                      Add Missing Transaction
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Record a transaction you forgot to enter
                    </p>
                  </div>
                </div>
              </button>
              <button
                onClick={handleCreateReconciliationAdjustment}
                className="w-full p-16 bg-background hover:bg-muted rounded-lg border border-border transition-smooth text-left"
              >
                <div className="flex items-start gap-12">
                  <div className="p-8 bg-accent/10 rounded-lg">
                    <Icon name="Settings" size={20} color="var(--color-accent)" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-foreground mb-4">
                      Create Reconciliation Adjustment
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Automatically adjust the balance to match your statement
                    </p>
                  </div>
                </div>
              </button>
            </div>
            <div className="p-24 border-t border-border">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowReconciliationOptions(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Add Transaction Form Modal */}
      {showAddTransactionForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
          <div className="bg-card rounded-lg shadow-elevation-4 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-24 border-b border-border">
              <h3 className="text-lg font-heading font-semibold text-card-foreground mb-8">
                Add Missing Transaction
              </h3>
              <p className="text-sm text-muted-foreground">
                Enter the transaction details to balance your account
              </p>
            </div>
            <div className="p-24 space-y-16">
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Date *
                </label>
                <Input
                  type="date"
                  value={newTransactionForm?.date}
                  onChange={(e) => setNewTransactionForm(prev => ({ ...prev, date: e?.target?.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Description *
                </label>
                <Input
                  type="text"
                  value={newTransactionForm?.description}
                  onChange={(e) => setNewTransactionForm(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Enter description"
                />
              </div>
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Type *
                </label>
                <Select
                  value={newTransactionForm?.type}
                  onChange={(value) => setNewTransactionForm(prev => ({ ...prev, type: value, categoryId: '' }))}
                  options={[
                    { value: 'income', label: 'Income' },
                    { value: 'expense', label: 'Expense' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Amount *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={newTransactionForm?.amount}
                  onChange={(e) => setNewTransactionForm(prev => ({ ...prev, amount: e?.target?.value }))}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Category
                </label>
                <Select
                  value={newTransactionForm?.categoryId}
                  onChange={(value) => setNewTransactionForm(prev => ({ ...prev, categoryId: value }))}
                  options={[
                    { value: '', label: 'Select category' },
                    ...filteredCategories?.map(cat => ({
                      value: cat?.id,
                      label: cat?.name
                    }))
                  ]}
                  disabled={loadingCategories}
                />
              </div>
            </div>
            <div className="p-24 border-t border-border flex gap-12">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowAddTransactionForm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                fullWidth
                onClick={handleSubmitNewTransaction}
              >
                Add Transaction
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReconciliationPanel;